import { styled } from "@mui/material/styles";
import { keyframes } from "@mui/system";

import { COLOR_WHITE } from "~/theme/colors";
import { getFormatMedia } from "~/theme/utils";

import { SEARCH_PANEL_DESKTOP_CLASSES } from "./constants";

export interface UseStylesProps {
  offset?: number;
  isInfoBanner?: boolean;
  isSearchOpen?: boolean;
  iconsCount?: number;
  panelPosition?: number;
}

const ICON_WIDTH = 48;

const fadeInEffect = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const slideInEffect = keyframes`
  0% {
    transform: scale(1, 0);
  }
  100% {
    transform: scale(1, 1);
  }
`;

export const SearchPanelDesktopContainer = styled("div")<UseStylesProps>(({
  theme,
  isSearchOpen,
  iconsCount,
  panelPosition,
}) => {
  const barHeight = 52;
  const fieldWideningTimeMs = 200;
  const panelSlidingTimeMs = 250;
  const visibleDrawerContentStyles = {
    animation: `${panelSlidingTimeMs}ms ${fieldWideningTimeMs}ms both ${slideInEffect}`,
  };
  const visibleBackdropStyles = {
    animation: `${panelSlidingTimeMs}ms ${fieldWideningTimeMs}ms backwards ${fadeInEffect}`,
    opacity: 1,
  };

  const fieldOpeningEffect = keyframes`
    from {
      left: calc(100% - ${(iconsCount ?? 0) * ICON_WIDTH}px - ${theme.spacing(1)} - 62.5px);
      width: 125px;
    }
    to {
      left: 50%;
      width: 50%;
    }
  `;

  const { BREAKPOINT_DESKTOP } = getFormatMedia(theme);
  return {
    [`&:has(> .${SEARCH_PANEL_DESKTOP_CLASSES.nativePopover}:popover-open) > .${SEARCH_PANEL_DESKTOP_CLASSES.toolbarWrapper}`]:
      {
        visibility: "hidden",
      },
    [`& .${SEARCH_PANEL_DESKTOP_CLASSES.toolbarWrapper}`]: {
      marginTop: theme.spacing(1.75),
      marginBottom: theme.spacing(1.75),
      [BREAKPOINT_DESKTOP]: {
        display: "flex",
        flexFlow: "row-reverse",
      },
    },
    [`& .${SEARCH_PANEL_DESKTOP_CLASSES.toolbar}`]: {
      width: "100%",
      height: barHeight,
      position: "relative",
      zIndex: 100,
      [BREAKPOINT_DESKTOP]: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        width: isSearchOpen ? "100%" : "50%",
      },
    },
    [`& .${SEARCH_PANEL_DESKTOP_CLASSES.logoCentered}`]: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      [BREAKPOINT_DESKTOP]: {
        position: "relative",
        display: "inline-block",
        left: 0,
        top: 0,
        transform: "translate(-50%, 0)",
        flexShrink: 0,
      },
    },
    [`& .${SEARCH_PANEL_DESKTOP_CLASSES.logoLeft}`]: {
      position: "absolute",
      top: "50%",
      left: 0,
      transform: "translate(0, -50%)",
      animation: `${fadeInEffect} ${fieldWideningTimeMs}`,
    },
    [`& .${SEARCH_PANEL_DESKTOP_CLASSES.iconsWrapper}`]: {
      position: "absolute",
      right: "0",
      zIndex: 999,
      marginTop: theme.spacing(1),
      "& > *:not(:last-child)": {
        marginRight: theme.spacing(1),
      },
      [BREAKPOINT_DESKTOP]: {
        position: "relative",
        flexShrink: 0,
      },
    },
    [`& .${SEARCH_PANEL_DESKTOP_CLASSES.shoppingListButton}`]: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
    [`& .${SEARCH_PANEL_DESKTOP_CLASSES.searchFieldRight}`]: {
      zIndex: 1,
      maxWidth: "50%",
      position: "absolute",
      top: "50%",
      right: iconsCount ? ICON_WIDTH * iconsCount : 0,
      transform: "translate(0, -50%)",
      [BREAKPOINT_DESKTOP]: {
        position: "relative",
        top: "0",
        right: "0 !important",
        display: "flex",
        justifyContent: "flex-end",
        maxWidth: "none",
        marginLeft: "auto",
        marginRight: theme.spacing(1),
        transform: "none",
      },
    },
    [`& .${SEARCH_PANEL_DESKTOP_CLASSES.searchFieldTriggerWrapper}`]: {
      cursor: "pointer",
    },
    [`& .${SEARCH_PANEL_DESKTOP_CLASSES.searchFieldTrigger}`]: {
      appearance: "none",
      background: "transparent",
      border: 0,
      borderRadius: "inherit",
      cursor: "pointer",
      inset: 0,
      padding: 0,
      position: "absolute",
      zIndex: 2,
      "&:focus-visible": {
        outline: `2px solid ${theme.palette.text.primary}`,
        outlineOffset: 2,
      },
    },
    [`& .${SEARCH_PANEL_DESKTOP_CLASSES.searchFieldCentered}`]: {
      maxWidth: "50%",
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      transition: `all ${fieldWideningTimeMs}ms`,
    },
    [`& .${SEARCH_PANEL_DESKTOP_CLASSES.closeButton}`]: {
      position: "absolute",
      right: 0,
      top: "50%",
      transform: "translateY(-50%)",
      animation: `${fadeInEffect} ${fieldWideningTimeMs}ms`,
    },
    [`& .${SEARCH_PANEL_DESKTOP_CLASSES.drawerContent}`]: {
      backgroundColor: COLOR_WHITE,
      width: "100%",
      position: "absolute",
      minHeight: "calc(min(100%, max(80%, 580px)))",
      boxShadow: `0px 12px 12px rgb(0 0 0 / 15%)`,
      transformOrigin: "top",
      paddingTop: theme.spacing(1.25),
      paddingBottom: theme.spacing(1.25),
      transition: "transform 225ms cubic-bezier(0, 0, 0.2, 1) 0ms",
      overflowY: "auto",
      WebkitOverflowScrolling: "touch",
      transform: "scale(1, 0)",
    },
    // Unsupported pseudo-classes invalidate an entire comma-separated selector list.
    [`& .${SEARCH_PANEL_DESKTOP_CLASSES.drawerVisible} .${SEARCH_PANEL_DESKTOP_CLASSES.drawerContent}`]:
      visibleDrawerContentStyles,
    [`& .${SEARCH_PANEL_DESKTOP_CLASSES.nativePopover}:popover-open .${SEARCH_PANEL_DESKTOP_CLASSES.drawerContent}`]:
      visibleDrawerContentStyles,
    [`& .${SEARCH_PANEL_DESKTOP_CLASSES.drawer}`]: {
      position: "fixed",
      zIndex: theme.zIndex.modal,
      inset: 0,
      top: `calc(${panelPosition}px + var(--info-banner-height, 0px))`,
      display: "none",
      visibility: "hidden",
      pointerEvents: "none",
    },
    [`& .${SEARCH_PANEL_DESKTOP_CLASSES.drawer}:not(.${SEARCH_PANEL_DESKTOP_CLASSES.nativePopover})`]:
      {
        [BREAKPOINT_DESKTOP]: {
          display: "block",
        },
      },
    [`& .${SEARCH_PANEL_DESKTOP_CLASSES.nativePopover}`]: {
      top: "calc(8px + var(--info-banner-height, 0px))",
      background: "transparent",
      border: 0,
      height: "auto",
      margin: 0,
      maxHeight: "none",
      maxWidth: "none",
      padding: 0,
      width: "100%",
      [`& .${SEARCH_PANEL_DESKTOP_CLASSES.toolbar}`]: {
        width: "100%",
      },
      [`& .${SEARCH_PANEL_DESKTOP_CLASSES.logoLeft}, & .${SEARCH_PANEL_DESKTOP_CLASSES.closeButton}`]:
        {
          animation: "none",
        },
      "&:popover-open": {
        [`& .${SEARCH_PANEL_DESKTOP_CLASSES.searchFieldCentered}`]: {
          animation: `${fieldOpeningEffect} ${fieldWideningTimeMs}ms ease both`,
        },
        [`& .${SEARCH_PANEL_DESKTOP_CLASSES.logoLeft}, & .${SEARCH_PANEL_DESKTOP_CLASSES.closeButton}`]:
          {
            animation: `${fadeInEffect} ${fieldWideningTimeMs}ms ease both`,
          },
      },
      "@media (prefers-reduced-motion: reduce)": {
        [`&:popover-open .${SEARCH_PANEL_DESKTOP_CLASSES.searchFieldCentered}, &:popover-open .${SEARCH_PANEL_DESKTOP_CLASSES.logoLeft}, &:popover-open .${SEARCH_PANEL_DESKTOP_CLASSES.closeButton}, &:popover-open .${SEARCH_PANEL_DESKTOP_CLASSES.drawerContent}, &:popover-open .${SEARCH_PANEL_DESKTOP_CLASSES.modalBackdrop}`]:
          {
            animationDuration: "0ms",
            animationDelay: "0ms",
          },
      },
      [`& .${SEARCH_PANEL_DESKTOP_CLASSES.drawerContent}`]: {
        top: 96,
      },
      [`& .${SEARCH_PANEL_DESKTOP_CLASSES.modalBackdrop}`]: {
        top: 96,
      },
      [BREAKPOINT_DESKTOP]: {
        "&:popover-open": {
          display: "block",
          pointerEvents: "auto",
          visibility: "visible",
        },
      },
    },
    [`& .${SEARCH_PANEL_DESKTOP_CLASSES.popoverHeader}`]: {
      backgroundColor: COLOR_WHITE,
      height: 96,
      paddingTop: theme.spacing(1.75),
      boxSizing: "border-box",
    },
    [`& .${SEARCH_PANEL_DESKTOP_CLASSES.popoverToolbarWrapper}`]: {
      maxWidth: 1920,
      marginInline: "auto",
      boxSizing: "border-box",
    },
    [`& .${SEARCH_PANEL_DESKTOP_CLASSES.drawer}.${SEARCH_PANEL_DESKTOP_CLASSES.drawerVisible}`]:
      {
        visibility: "visible",
        pointerEvents: "auto",
      },
    [`& .SearchPanel-element`]: {
      border: "1.5px solid transparent",
    },
    [`& .${SEARCH_PANEL_DESKTOP_CLASSES.modalBackdrop}`]: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: "flex",
      zIndex: -1,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "rgba(0, 0, 0, 0.25)",
      WebkitTapHighlightColor: "transparent",
      opacity: 0,
    },
    [`& .${SEARCH_PANEL_DESKTOP_CLASSES.drawerVisible} .${SEARCH_PANEL_DESKTOP_CLASSES.modalBackdrop}`]:
      visibleBackdropStyles,
    [`& .${SEARCH_PANEL_DESKTOP_CLASSES.nativePopover}:popover-open .${SEARCH_PANEL_DESKTOP_CLASSES.modalBackdrop}`]:
      visibleBackdropStyles,
    [`& .${SEARCH_PANEL_DESKTOP_CLASSES.accountButton}`]: {
      padding: theme.spacing(1),
    },
  };
});
